import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Users,
  MessageCircle,
  Clock,
  Heart,
  Shield,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Zap,
  UserCheck,
  Copy,
  Share2
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
// import api from '../utils/api'

/**
 * TwoPlayerSimulation Component
 * Cooperative survival scenarios where two players make decisions together
 * Players alternate making choices and can discuss via chat
 */

const TwoPlayerSimulation = () => {
  const { simulationId, sessionId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  
  // Game state
  const [gameSession, setGameSession] = useState(null)
  const [currentDecision, setCurrentDecision] = useState(null)
  const [playerState, setPlayerState] = useState({
    health: 100,
    safety: 50,
    timeRemaining: 15
  })
  const [partner, setPartner] = useState(null)
  const [isMyTurn, setIsMyTurn] = useState(false)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [showChat, setShowChat] = useState(true)
  const [decisionsHistory, setDecisionsHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [joinCode, setJoinCode] = useState('')
  const [waitingForPartner, setWaitingForPartner] = useState(true)

  // Initialize or join session
  useEffect(() => {
    if (sessionId) {
      joinSession()
    } else {
      createSession()
    }

    // Simulate real-time updates (in production, use WebSockets)
    const interval = setInterval(checkSessionUpdates, 2000)
    return () => clearInterval(interval)
  }, [sessionId])

  /**
   * Creates a new 2-player session
   */
  const createSession = async () => {
    try {
      // Generate a unique session code
      const code = Math.random().toString(36).substring(2, 8).toUpperCase()
      setJoinCode(code)
      
      // Mock session creation
      const session = {
        id: code,
        simulationId,
        host: user.username,
        players: [user.username],
        currentTurn: user.username,
        state: {
          health: 100,
          safety: 50,
          timeRemaining: 15,
          currentDecision: 'first-action'
        }
      }
      
      setGameSession(session)
      setIsMyTurn(true)
      setLoading(false)
    } catch (error) {
      console.error('Failed to create session:', error)
      toast.error('Failed to create game session')
    }
  }

  /**
   * Joins an existing session
   */
  const joinSession = async () => {
    try {
      // Mock joining session
      const session = {
        id: sessionId,
        simulationId: 'day-0-alert',
        host: 'OtherPlayer',
        players: ['OtherPlayer', user.username],
        currentTurn: 'OtherPlayer',
        state: {
          health: 100,
          safety: 50,
          timeRemaining: 15,
          currentDecision: 'first-action'
        }
      }
      
      setGameSession(session)
      setPartner({ username: 'OtherPlayer' })
      setIsMyTurn(false)
      setWaitingForPartner(false)
      setLoading(false)
      
      // Add join message
      addSystemMessage(`${user.username} joined the survival mission!`)
    } catch (error) {
      console.error('Failed to join session:', error)
      toast.error('Failed to join game session')
    }
  }

  /**
   * Checks for session updates (mock real-time)
   */
  const checkSessionUpdates = () => {
    // In production, this would be WebSocket events
    if (waitingForPartner && gameSession?.players.length === 1) {
      // Simulate partner joining after 5 seconds
      setTimeout(() => {
        setPartner({ username: 'SurvivalBuddy' })
        setWaitingForPartner(false)
        addSystemMessage('SurvivalBuddy joined the survival mission!')
      }, 5000)
    }
  }

  /**
   * Handles player decision
   */
  const handleDecision = (option) => {
    if (!isMyTurn) {
      toast.error("Wait for your partner's turn!")
      return
    }

    // Update game state based on decision
    const outcome = option.outcome
    setPlayerState(prev => ({
      health: Math.max(0, Math.min(100, prev.health + (outcome.health || 0))),
      safety: Math.max(0, Math.min(100, prev.safety + (outcome.safety || 0))),
      timeRemaining: Math.max(0, prev.timeRemaining + (outcome.time || 0))
    }))

    // Add to history
    setDecisionsHistory(prev => [...prev, {
      player: user.username,
      decision: option.text,
      outcome: outcome.message
    }])

    // Add decision message to chat
    addSystemMessage(`${user.username} chose: ${option.text}`)
    addSystemMessage(outcome.message)

    // Switch turns
    setIsMyTurn(false)
    
    // Simulate partner's turn after 3 seconds
    setTimeout(() => {
      if (partner) {
        simulatePartnerDecision()
      }
    }, 3000)
  }

  /**
   * Simulates partner's decision (for demo)
   */
  const simulatePartnerDecision = () => {
    const options = [
      { text: "Check supplies", outcome: { safety: 10, message: "Found extra water!" } },
      { text: "Secure the area", outcome: { safety: 20, time: -1, message: "Area secured!" } },
      { text: "Call for help", outcome: { time: -2, message: "Help might be coming..." } }
    ]
    
    const choice = options[Math.floor(Math.random() * options.length)]
    
    addSystemMessage(`${partner.username} chose: ${choice.text}`)
    addSystemMessage(choice.outcome.message)
    
    setPlayerState(prev => ({
      health: Math.max(0, Math.min(100, prev.health + (choice.outcome.health || 0))),
      safety: Math.max(0, Math.min(100, prev.safety + (choice.outcome.safety || 0))),
      timeRemaining: Math.max(0, prev.timeRemaining + (choice.outcome.time || 0))
    }))
    
    setIsMyTurn(true)
  }

  /**
   * Adds a system message to chat
   */
  const addSystemMessage = (text) => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'system',
      text,
      timestamp: new Date()
    }])
  }

  /**
   * Sends a chat message
   */
  const sendMessage = (e) => {
    e.preventDefault()
    if (!newMessage.trim()) return

    const message = {
      id: Date.now(),
      type: 'player',
      sender: user.username,
      text: newMessage,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, message])
    setNewMessage('')
  }

  /**
   * Copies join code to clipboard
   */
  const copyJoinCode = () => {
    navigator.clipboard.writeText(joinCode)
    toast.success('Join code copied!')
  }

  /**
   * Shares the game session
   */
  const shareSession = () => {
    const shareUrl = `${window.location.origin}/2player/${simulationId}/${joinCode}`
    const shareText = `Join me in a 2-player survival simulation! Use code: ${joinCode}`
    
    if (navigator.share) {
      navigator.share({
        title: 'SurviveIt 2-Player Mission',
        text: shareText,
        url: shareUrl
      })
    } else {
      navigator.clipboard.writeText(shareUrl)
      toast.success('Game link copied!')
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Setting up survival mission...</p>
        </div>
      </div>
    )
  }

  // Waiting for partner state
  if (waitingForPartner) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-gray-900 rounded-2xl p-8 text-center"
        >
          <Users className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">
            Waiting for Survival Partner
          </h2>
          <p className="text-gray-400 mb-6">
            Share this code with your partner:
          </p>
          
          {/* Join Code Display */}
          <div className="bg-gray-800 rounded-lg p-4 mb-6">
            <div className="text-3xl font-mono font-bold text-orange-400 mb-2">
              {joinCode}
            </div>
            <button
              onClick={copyJoinCode}
              className="flex items-center gap-2 mx-auto text-sm text-gray-400 hover:text-white transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copy Code
            </button>
          </div>

          {/* Share Options */}
          <div className="flex gap-3 justify-center mb-6">
            <button
              onClick={shareSession}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Share Link
            </button>
            <button
              onClick={() => navigate('/simulations')}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>

          {/* Loading Animation */}
          <div className="flex justify-center gap-2">
            <motion.div
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-2 h-2 bg-orange-500 rounded-full"
            />
            <motion.div
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.5, delay: 0.5, repeat: Infinity }}
              className="w-2 h-2 bg-orange-500 rounded-full"
            />
            <motion.div
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.5, delay: 1, repeat: Infinity }}
              className="w-2 h-2 bg-orange-500 rounded-full"
            />
          </div>
        </motion.div>
      </div>
    )
  }

  // Main game interface
  return (
    <div className="min-h-screen bg-gray-950 flex">
      {/* Game Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-gray-900 border-b border-gray-800 p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/simulations')}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Leave Mission
            </button>
            
            <div className="flex items-center gap-4">
              {/* Player Indicators */}
              <div className="flex items-center gap-2">
                <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${
                  isMyTurn ? 'bg-green-900' : 'bg-gray-800'
                }`}>
                  <UserCheck className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-white">You</span>
                </div>
                <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${
                  !isMyTurn ? 'bg-green-900' : 'bg-gray-800'
                }`}>
                  <UserCheck className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-white">{partner?.username}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status Bars */}
        <div className="bg-gray-900 bg-opacity-50 p-4">
          <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
            <StatusBar
              icon={Heart}
              label="Health"
              value={playerState.health}
              color="red"
            />
            <StatusBar
              icon={Shield}
              label="Safety"
              value={playerState.safety}
              color="blue"
            />
            <StatusBar
              icon={Clock}
              label="Time"
              value={playerState.timeRemaining}
              max={15}
              color="yellow"
              unit=" min"
            />
          </div>
        </div>

        {/* Decision Area */}
        <div className="flex-1 p-6 max-w-4xl mx-auto w-full">
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-gray-900 rounded-xl p-6"
            >
              {/* Turn Indicator */}
              <div className="mb-6 text-center">
                {isMyTurn ? (
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-900 rounded-full">
                    <Zap className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-medium">Your Turn!</span>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 rounded-full">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-400">Waiting for {partner?.username}...</span>
                  </div>
                )}
              </div>

              {/* Scenario Text */}
              <h2 className="text-xl font-bold text-white mb-6">
                The missile alert is real. You and {partner?.username} must work together to survive.
                What's your next move?
              </h2>

              {/* Decision Options */}
              <div className="grid gap-4">
                {[
                  {
                    text: "Split up to gather supplies",
                    outcome: { safety: -10, time: -1, message: "Risky but efficient!" }
                  },
                  {
                    text: "Stay together and move to shelter",
                    outcome: { safety: 20, time: -2, message: "Safety in numbers!" }
                  },
                  {
                    text: "One scouts while other prepares",
                    outcome: { safety: 10, time: -1, message: "Balanced approach!" }
                  }
                ].map((option, index) => (
                  <motion.button
                    key={index}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleDecision(option)}
                    disabled={!isMyTurn}
                    className={`p-4 rounded-lg text-left transition-all ${
                      isMyTurn
                        ? 'bg-gray-800 hover:bg-gray-700 cursor-pointer'
                        : 'bg-gray-800 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <p className="text-white">{option.text}</p>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Chat Sidebar */}
      <div className={`bg-gray-900 border-l border-gray-800 transition-all duration-300 ${
        showChat ? 'w-80' : 'w-0'
      }`}>
        {showChat && (
          <div className="h-full flex flex-col">
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Team Chat
                </h3>
                <button
                  onClick={() => setShowChat(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg) => (
                <div key={msg.id}>
                  {msg.type === 'system' ? (
                    <div className="text-center">
                      <span className="text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded">
                        {msg.text}
                      </span>
                    </div>
                  ) : (
                    <div className={`${
                      msg.sender === user.username ? 'text-right' : 'text-left'
                    }`}>
                      <div className={`inline-block max-w-[80%] p-3 rounded-lg ${
                        msg.sender === user.username
                          ? 'bg-orange-600 text-white'
                          : 'bg-gray-800 text-white'
                      }`}>
                        <p className="text-sm">{msg.text}</p>
                        <p className="text-xs opacity-70 mt-1">
                          {msg.sender}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Message Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-gray-800">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 bg-gray-800 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Send
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Chat Toggle (when hidden) */}
      {!showChat && (
        <button
          onClick={() => setShowChat(true)}
          className="fixed right-4 bottom-4 p-3 bg-orange-500 text-white rounded-full shadow-lg hover:bg-orange-600 transition-colors"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
    </div>
  )
}

/**
 * StatusBar Component
 * Displays health, safety, or time status
 */
const StatusBar = ({ icon: Icon, label, value, max = 100, color, unit = '%' }) => {
  const percentage = (value / max) * 100
  
  const colorClasses = {
    red: 'bg-red-500',
    blue: 'bg-blue-500',
    yellow: 'bg-yellow-500'
  }

  return (
    <div className="bg-gray-800 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 text-${color}-400`} />
        <span className="text-sm text-gray-400">{label}</span>
      </div>
      <div className="bg-gray-700 rounded-full h-2 overflow-hidden mb-1">
        <motion.div
          className={colorClasses[color]}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5 }}
          style={{ height: '100%' }}
        />
      </div>
      <p className="text-sm text-white font-medium">
        {value}{unit}
      </p>
    </div>
  )
}

export default TwoPlayerSimulation